import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withPreloading, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { TranslateModule, TranslateLoader, MissingTranslationHandler, TranslateCompiler } from '@ngx-translate/core';
import { CustomMissingTranslationHandler } from './core/handlers/missing-translation.handler';
import { LegacySafeMessageFormatCompiler } from './core/i18n/legacy-safe-message-format.compiler';
import { importProvidersFrom } from '@angular/core';
import { MessageService } from 'primeng/api';
import { definePreset } from '@primeuix/themes';
import { appRoutes } from './app.routes';
import { provideInfrastructure } from './core/providers/infrastructure.providers';
import { provideDomain } from './core/providers/domain';
import { AuthDatabaseInitializer } from './core/services/auth-database-initializer';
import { AppShellBootstrapService } from './core/services/app-shell-bootstrap.service';
import { InfrastructureBootstrapService } from './core/services/infrastructure-bootstrap.service';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';
import { provideNgIdle, IdleExpiry, LocalStorageExpiry, LocalStorage } from '@ng-idle/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { provideMonobankPlugin } from '@app/plugins/monobank';
import { providePumbPlugin } from '@app/plugins/pumb';
import { provideSantanderPlugin } from '@app/plugins/santander';
import { AI_SESSION_HOST, provideAiSessionPlugin } from '@app/plugins/ai-session';
import { NotificationService } from './core/services/notification.service';
import { AppStore } from './core/states/app.store';
import { AppErrorHandler } from './core/services/app-error-handler.service';
import { InactivityService } from './core/services/inactivity.service';
import { environment } from '@app/environment';
import {
  ERROR_HANDLER_PORT,
  IDLE_CONTROL_PORT,
  NOTIFICATION_PORT,
  type PluginTranslationAsset,
  PLUGIN_TRANSLATION_ASSETS,
} from '@app/ui/shared';
import Aura from '@primeuix/themes/aura';
import { RuntimeConfigService } from './core/services/runtime-config.service';
import { LogStoreService } from './core/services/log-store.service';
import { PluginAwareTranslateLoader } from './core/services/plugin-aware-translate-loader';
import { REPORT_EXPORT_USE_CASES } from './features/reports/report-viewer/export/report-export.use-case';
import { ExportExpensesIncomesUseCase } from './features/reports/report-viewer/export/export-expenses-incomes.use-case';
import { ExportGroupingByDatesUseCase } from './features/reports/report-viewer/export/export-grouping-by-dates.use-case';
import { ExportAccountBalancesUseCase } from './features/reports/report-viewer/export/export-account-balances.use-case';
import { ExportStatementsUseCase } from './features/reports/report-viewer/export/export-statements.use-case';
import { SelectivePreloadingStrategy } from './core/routing/selective-preloading.strategy';
import { RUNTIME_KIND } from './core/tokens/infrastructure.tokens';
import { detectRuntimeKind, shouldEnableServiceWorker } from './core/runtime/runtime-kind';
import { APP_UPDATE_ADAPTER } from './core/services/update-adapters/update-adapter';
import { createUpdateAdapter } from './core/services/update-adapters/create-update-adapter';
import { SwUpdate } from '@angular/service-worker';
import { Router } from '@angular/router';

const runtimeKind = detectRuntimeKind();

const AppAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{cyan.50}',
      100: '{cyan.100}',
      200: '{cyan.200}',
      300: '{cyan.300}',
      400: '{cyan.400}',
      500: '{cyan.500}',
      600: '{cyan.600}',
      700: '{cyan.700}',
      800: '{cyan.800}',
      900: '{cyan.900}',
      950: '{cyan.950}',
    },
    colorScheme: {
      dark: {
        surface: {
          0: '{stone.0}',
          50: '{stone.50}',
          100: '{stone.100}',
          200: '{stone.200}',
          300: '{stone.300}',
          400: '{stone.400}',
          500: '{stone.500}',
          600: '{stone.600}',
          700: '{stone.700}',
          800: '{stone.800}',
          900: '{stone.900}',
          950: '{stone.950}',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: RUNTIME_KIND, useValue: runtimeKind },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideAnimationsAsync(),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        fallbackLang: 'en',
        lang: 'en',
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: CustomMissingTranslationHandler,
        },
        compiler: {
          provide: TranslateCompiler,
          useClass: LegacySafeMessageFormatCompiler,
        },
      }),
    ),
    {
      provide: TranslateLoader,
      useFactory: () =>
        new PluginAwareTranslateLoader(
          inject(HttpClient),
          (inject(PLUGIN_TRANSLATION_ASSETS, { optional: true }) as readonly PluginTranslationAsset[] | null) ?? [],
        ),
    },
    providePrimeNG({
      ripple: true,
      theme: {
        preset: AppAura,
        options: {
          darkModeSelector: '.p-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, properties, components, overrides, utilities, keyframes',
          },
        },
      },
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withPreloading(SelectivePreloadingStrategy),
      withViewTransitions(/*{ skipInitialTransition: true }*/),
    ),
    {
      provide: APP_UPDATE_ADAPTER,
      useFactory: createUpdateAdapter,
      deps: [RUNTIME_KIND, SwUpdate, Router, RuntimeConfigService],
    },
    MessageService,
    provideNgIdle(),
    {
      provide: IdleExpiry,
      useFactory: (localStorage: LocalStorage) => {
        const expiry = new LocalStorageExpiry(localStorage);
        expiry.setIdleName('fin');
        return expiry;
      },
      deps: [LocalStorage],
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: shouldEnableServiceWorker(runtimeKind, isDevMode()),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAppInitializer(() => {
      inject(LogStoreService).patchConsole();
    }),
    provideAppInitializer(async () => {
      await inject(RuntimeConfigService).load();
    }),
    provideAppInitializer(async () => {
      await inject(AuthDatabaseInitializer).initialize();
    }),
    provideAppInitializer(async () => {
      await inject(InfrastructureBootstrapService).initialize();
    }),
    provideAppInitializer(() => {
      inject(AppShellBootstrapService).initialize();
    }),
    ...provideInfrastructure(),
    ...provideDomain(),
    { provide: ERROR_HANDLER_PORT, useExisting: AppErrorHandler },
    { provide: IDLE_CONTROL_PORT, useExisting: InactivityService },
    { provide: NOTIFICATION_PORT, useExisting: NotificationService },
    ...provideMonobankPlugin({
      apiUrl: environment['monobankApiUrl'] as string | undefined,
      rateLimitMs: environment['monobankRateLimitMs'] as number | undefined,
    }),
    ...providePumbPlugin(),
    ...provideSantanderPlugin(),
    {
      provide: AI_SESSION_HOST,
      useFactory: (store: InstanceType<typeof AppStore>, notification: NotificationService) => ({
        isAuthorized: () => store.isAuthorized(),
        isLeader: () => store.isLeader(),
        confirmPassword: () => notification.confirmPassword(),
      }),
      deps: [AppStore, NotificationService],
    },
    ...provideAiSessionPlugin(),
    { provide: REPORT_EXPORT_USE_CASES, useExisting: ExportExpensesIncomesUseCase, multi: true },
    { provide: REPORT_EXPORT_USE_CASES, useExisting: ExportGroupingByDatesUseCase, multi: true },
    { provide: REPORT_EXPORT_USE_CASES, useExisting: ExportAccountBalancesUseCase, multi: true },
    { provide: REPORT_EXPORT_USE_CASES, useExisting: ExportStatementsUseCase, multi: true },
    ConfirmationService,
    DialogService,
  ],
};
